/**
 * The formula bar (§15).
 *
 * The grid could select a cell and the handler could set one; nothing joined
 * them. This is that join, and it carries the one thing §15 insists on being
 * honest about.
 *
 * **Modu Viewer has no calculation engine.** A formula cell shows its last
 * saved result, and this bar shows the formula that produced it — but typing
 * over that formula replaces it with a literal value, because the alternative
 * would be to write a formula whose result the app cannot compute. §19 says
 * not to offer what cannot be honoured, so the bar says what will happen
 * *before* the edit, not in a dialog afterwards.
 */
import { useEffect, useRef, useState } from 'react';
import type { XlsxCell } from '@moduviewer/handlers';
import { Icon } from '@moduviewer/design/icon';

export interface FormulaBarProps {
  readonly selected: string | null;
  readonly cell: XlsxCell | null;
  readonly editable: boolean;
  readonly onCommit: (ref: string, value: string) => void;
  /** Move the selection after Enter, the way a spreadsheet does. */
  readonly onCommitAndMove: (ref: string, value: string) => void;
}

export function FormulaBar({
  selected, cell, editable, onCommit, onCommitAndMove,
}: FormulaBarProps): React.JSX.Element {
  const [draft, setDraft] = useState('');
  const [dirty, setDirty] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  // Selecting a different cell abandons an uncommitted draft, which is what a
  // spreadsheet does and what the click meant.
  useEffect(() => {
    setDraft(cell?.formula ? `=${cell.formula}` : cell?.text ?? '');
    setDirty(false);
  }, [selected, cell]);

  const commit = (move: boolean): void => {
    if (!selected || !dirty) return;
    // A leading `=` is stripped: the value is stored literally, and keeping
    // the sign would put a formula the app cannot compute back in the file.
    const value = draft.startsWith('=') ? draft.slice(1) : draft;
    (move ? onCommitAndMove : onCommit)(selected, value);
    setDirty(false);
  };

  const replacingFormula = editable && dirty && cell?.formula != null;

  return (
    <div className="formula">
      <span className="formula__ref tnum" aria-label="선택한 칸">
        {selected ?? '—'}
      </span>
      <span className="sep" />

      <label className="visually-hidden" htmlFor="formula-input">칸 내용</label>
      <input
        id="formula-input"
        ref={input}
        className="formula__input"
        value={draft}
        readOnly={!editable || !selected}
        placeholder={selected ? (editable ? '값을 입력하세요' : '읽기 전용') : '칸을 선택하세요'}
        onChange={(e) => { setDraft(e.target.value); setDirty(true); }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); commit(true); }
          if (e.key === 'Escape') {
            e.preventDefault();
            setDraft(cell?.formula ? `=${cell.formula}` : cell?.text ?? '');
            setDirty(false);
            input.current?.blur();
          }
        }}
        onBlur={() => commit(false)}
      />

      {replacingFormula ? (
        <span className="formula__warn">
          <Icon name="alert" size={13} />
          {/* Said before the edit lands, not in a dialog after it. */}
          수식이 입력값으로 대체됩니다
        </span>
      ) : cell?.formula ? (
        <span className="formula__note">
          <Icon name="info" size={13} />
          마지막 저장된 결과: {cell.text || '(빈 값)'}
        </span>
      ) : null}
    </div>
  );
}
